import { type FunctionComponent, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { SendIntakeFormPayload, SendIntakeFormResult } from '@spike/form-model';
import { FormsStatus, sendIntakeFormThunk } from '@spike/forms-action';
import useNonInitialEffect from '@versiondos/hooks';
import { showErrorThunk } from '@spike/notifications-action';
import { formatPhoneNumberByLength } from 'components/SMS/TwoWay/ProspectMessage/utils';

import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import Button from 'components/UI/V3/Button/Button';

import TextFieldWithButton from 'components/UI/TextFieldWithButton';

import { type InputItem, MultipleInputField } from './ui/MultipleInputField';
import Channel from './ui/Channel';
import MobileCloseButton from './ui/MobileCloseButton';

import styles from './ShareSendPanel.module.scss';

interface ShareSendPanelProps {
    isOpen: boolean;
    onClose: () => void;
    token?: string;
    email?: string;
    phone?: string;
}

type ChannelType = 'email' | 'sms';

export const ShareSendPanel: FunctionComponent<ShareSendPanelProps> = ({ isOpen, onClose, token, email, phone }) => {
    const marketplace = useMarketplace();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const status = useSelector<RootState, FormsStatus>(state => state.forms.status);
    const loading = useSelector<RootState, boolean>(state => state.forms.loading);

    const sendIntakeResult = useSelector<RootState, SendIntakeFormResult | null | undefined>(
        state => state.forms.sendIntakeResult
    );

    const [selectedChannel, setSelectedChannel] = useState<ChannelType>('email');
    const [items, setItems] = useState<InputItem[]>([]);
    const [itemError, setItemError] = useState('');

    const [showResults, setShowResults] = useState(false);

    const [urlCopied, setUrlCopied] = useState(false);
    const [buttonIcon, setButtonIcon] = useState(faCopy);
    const [buttonMessage, setButtonMessage] = useState('Copy');

    const intakeFormUrl = `${process.env.REACT_APP_ONLINE_BOOKING_HOST_URL}${
        marketplace.onlineBooking.slug
    }/intake-form${token ? `?token=${token}` : ''}`;

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-+$$$$]+$/;

    const validateInput = (input: string): boolean => {
        const trimmedInput = input.trim();
        if (selectedChannel === 'email') {
            return emailRegex.test(trimmedInput);
        } else {
            // For phone: must be at least 10 digits (excluding formatting characters)
            const digitsOnly = trimmedInput.replace(/\D/g, '');
            return phoneRegex.test(trimmedInput) && digitsOnly.length >= 10;
        }
    };

    const validateInputWrapper = (value: string) => {
        // Re-use your existing validation logic
        const isValid = validateInput(value);

        if (isValid && selectedChannel === 'sms') {
            // Check numeric length only if it passed format validation
            const numericValue = value.replace(/\D/g, '');
            const MAX_PHONE_LENGTH = 15;

            // Return false if the numeric part exceeds 15 characters
            return numericValue.length <= MAX_PHONE_LENGTH;
        }

        return isValid;
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setShowResults(false);
        setItemError('');

        if (selectedChannel === 'email' && email) {
            setItems([{ id: crypto.randomUUID(), value: email, isValid: true }]);
        } else if (selectedChannel === 'sms' && phone) {
            setItems([{ id: crypto.randomUUID(), value: formatPhoneNumberByLength(phone), isValid: true }]);
        } else {
            setItems([]);
        }
    }, [isOpen, selectedChannel, email, phone]);

    useEffect(() => {
        if (urlCopied) {
            setButtonMessage('Copied');
            setButtonIcon(faCheck);
        }
    }, [urlCopied]);

    useNonInitialEffect(() => {
        if (status === FormsStatus.SendIntakeFormSuccess) {
            setShowResults(true);
        }

        if (status === FormsStatus.SendIntakeFormError) {
            dispatch(showErrorThunk('Error sending intake form.'));
        }
    }, [status]);

    const copyToClipboard = () => {
        setUrlCopied(true);
        if ('clipboard' in navigator) {
            navigator.clipboard.writeText(intakeFormUrl);
        } else {
            document.execCommand('copy', true, intakeFormUrl);
        }
    };

    const itemLabel = selectedChannel === 'email' ? 'email' : 'phone number';
    const itemLabelPlural = selectedChannel === 'email' ? 'email(s)' : 'phone number(s)';

    const renderSuccess = (
        <div className={styles.containerResults}>
            <FontAwesomeIcon
                icon={faCheckCircle}
                color="#5E8677"
                fontSize={94}
            />
            <span className={styles.titleResults}>Intake Form Sent</span>
            <span className={styles.subtitleResults}>
                The intake form was successfully sent to <strong>[{sendIntakeResult?.counts.sent}]</strong>{' '}
                {sendIntakeResult?.counts.sent === 0 || sendIntakeResult?.counts.sent === 1
                    ? itemLabel
                    : itemLabelPlural}
                .
            </span>
        </div>
    );

    const handleSend = () => {
        if (items.length === 0) {
            setItemError(`Please add at least one ${selectedChannel === 'email' ? 'email address' : 'phone number'}`);
            return;
        }

        // 1. Obtener la lista de valores válidos
        const validItems = items.filter(e => e.isValid).map(e => e.value);

        // 2. Construir el payload para la thunk
        const payload: SendIntakeFormPayload = {
            sentVia: selectedChannel as any
        };

        if (selectedChannel === 'email') {
            payload.emails = validItems;
        } else {
            payload.phones = validItems;
        }

        dispatch(sendIntakeFormThunk(apiClientWrapper as any, payload));
    };

    const clearView = () => {
        setShowResults(false);
        setItemError('');
        setItems([]);
    };

    const handleDone = () => {
        clearView();
        onClose();
    };

    const closeButton = <MobileCloseButton onClick={!showResults ? onClose : handleDone} />;

    if (!isOpen) return null;

    return (
        <Drawer onClose={onClose}>
            {!showResults ? (
                <>
                    <div className={styles.panel}>
                        <div className={styles.header}>
                            <h2 className={styles.title}>Send Intake Form</h2>
                            {closeButton}
                        </div>
                        <div className={styles.subtitle}>
                            Share your intake form link today to connect with clients and efficiently create their
                            profiles
                        </div>

                        <div className={styles.urlInput}>
                            <TextFieldWithButton
                                value={intakeFormUrl}
                                label={buttonMessage}
                                startIcon={buttonIcon}
                                blackButton={true}
                                onClick={copyToClipboard}
                                classNameCustom="onlineBookingInput"
                                readOnly={true}
                            />
                        </div>

                        {/* New Channel Component */}
                        <Channel
                            selectedChannel={selectedChannel}
                            onChannelChange={setSelectedChannel}
                        />

                        <div className="bg-white p-6 rounded-lg shadow-sm">
                            <MultipleInputField
                                items={items}
                                onItemsChange={setItems}
                                label={selectedChannel === 'email' ? 'Emails' : 'Phone Numbers'}
                                description={
                                    selectedChannel === 'email'
                                        ? 'Add multiple emails separated by commas to send the intake form.'
                                        : 'Add multiple phone numbers separated by commas to send the intake form.'
                                }
                                placeholder={
                                    selectedChannel === 'email'
                                        ? 'Ex. example@email.com, another@email.com'
                                        : 'Ex. (123) 456-7891, (123) 456-7891'
                                }
                                errorMessage={itemError}
                                onErrorChange={setItemError}
                                validateItem={validateInputWrapper}
                                separator=","
                                allowDuplicates={false}
                                formatItem={selectedChannel === 'sms' ? formatPhoneNumberByLength : undefined}
                                errorMessages={{
                                    emptyInput:
                                        selectedChannel === 'email'
                                            ? 'Please enter an email'
                                            : 'Please enter a phone number',
                                    invalidValues:
                                        selectedChannel === 'email'
                                            ? 'Please enter a valid email'
                                            : 'Please enter a valid phone number (max 15 numbers)',
                                    duplicateValues:
                                        selectedChannel === 'email'
                                            ? 'Duplicate email addresses'
                                            : 'Duplicate phone numbers',
                                    maxItemsReached: 'Maximum recipients reached'
                                }}
                                maxLength={selectedChannel === 'sms' ? 15 : undefined}
                            />
                        </div>
                    </div>
                    <div className={styles.footer}>
                        <Button
                            label="Send"
                            onClick={handleSend}
                            variant="green"
                            size="large"
                            fullWidth={true}
                            disabled={items.length === 0}
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.resultsPanel}>
                        <div className={styles.header}>
                            <h2 className={styles.title}>Send Intake Form</h2>
                            {closeButton}
                        </div>

                        <div className={styles.resultsContent}>
                            {renderSuccess}

                            {sendIntakeResult?.existing.length! > 0 && (
                                <div className={styles.failedSection}>
                                    <h4 className={styles.titleFailedSection}>Could Not Send - Already Exists</h4>
                                    <ul className={styles.itemsFailedSection}>
                                        {sendIntakeResult?.existing.map((item, index) => (
                                            <li
                                                className={styles.itemFailedSection}
                                                key={index}
                                            >
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <Button
                            label="Done"
                            onClick={handleDone}
                            variant="black"
                            size="large"
                            fullWidth={true}
                        />
                    </div>
                </>
            )}
        </Drawer>
    );
};

export default ShareSendPanel;
